import React from 'react';
import { useTranslation } from 'react-i18next';

const PRESS_ITEMS = [
  {
    id: 'sapo-sns',
    outlet: 'Sapo',
    category: 'opinion',
    titleKey: 'press.articles.sapoSns.title',
    titleFallback: 'SNS com inteligência artificial pode dar mais tempo aos médicos',
    descKey: 'press.articles.sapoSns.desc',
    descFallback: 'Opinion article on how AI can free up physician time in the Portuguese National Health Service.',
    url: 'https://sapo.pt/artigo/sns-com-inteligencia-artificial-pode-dar-mais-tempo-aos-medicos-6998834f4c6a066718d368fe',
    date: '2026',
  },
  {
    id: 'visao-seguro',
    outlet: 'Visão',
    category: 'opinion',
    titleKey: 'press.articles.visaoSeguro.title',
    titleFallback: 'O alerta de seguro sobre as análises de sangue no SNS',
    descKey: 'press.articles.visaoSeguro.desc',
    descFallback: 'A safety perspective on blood test analysis in the Portuguese health system.',
    url: 'https://visao.pt/opiniao/ponto-de-vista/2026-01-31-o-alerta-de-seguro-sobre-as-analises-de-sangue-no-sns/',
    date: 'Jan 2026',
  },
  {
    id: 'eco-seed',
    outlet: 'ECO',
    category: 'funding',
    titleKey: 'press.articles.ecoSeed.title',
    titleFallback: 'Ex-atleta do Sporting levanta 1,2 milhões para acelerar análises ao sangue com IA',
    descKey: 'press.articles.ecoSeed.desc',
    descFallback: 'BloodFlow raises €1.2M seed round to accelerate AI-powered blood test analysis.',
    url: 'https://eco.sapo.pt/2025/07/30/ex-atleta-do-sporting-levanta-12-milhoes-para-acelerar-analises-ao-sangue-com-inteligencia-artificial/',
    date: 'Jul 2025',
  },
  {
    id: 'tnbi-seed',
    outlet: 'The Next Big Idea',
    category: 'funding',
    titleKey: 'press.articles.tnbiSeed.title',
    titleFallback: 'BloodFlow fecha ronda seed de 1,2 milhões para levar IA às análises de sangue',
    descKey: 'press.articles.tnbiSeed.desc',
    descFallback: 'Seed round coverage — BloodFlow brings AI to blood test interpretation.',
    url: 'https://thenextbigidea.pt/bloodflow-fecha-ronda-seed-de-12-milhoes-para-levar-ia-as-analises-de-sangue/',
    date: 'Jul 2025',
  },
  {
    id: 'eu-startups',
    outlet: 'EU-Startups',
    category: 'recognition',
    titleKey: 'press.articles.euStartups.title',
    titleFallback: '10 of the most promising Portuguese startups to keep an eye on',
    descKey: 'press.articles.euStartups.desc',
    descFallback: 'BloodFlow named one of the 10 most promising Portuguese startups by EU-Startups.',
    url: 'https://www.eu-startups.com/2026/02/inside-portugals-next-startup-wave-10-of-the-most-promising-startups-to-keep-an-eye-on/',
    date: 'Feb 2026',
  },
];

const categoryLabels = {
  opinion: { en: 'Opinion', pt: 'Opinião' },
  funding: { en: 'Funding', pt: 'Investimento' },
  recognition: { en: 'Recognition', pt: 'Reconhecimento' },
};

const PressCard = ({ item, t, lang }) => {
  const catLabel = categoryLabels[item.category]?.[lang] || categoryLabels[item.category]?.en;

  return (
    <a
      href={item.url}
      target="_blank"
      rel="noopener noreferrer"
      className="bf-press-card"
      style={{
        display: 'flex',
        flexDirection: 'column',
        padding: '24px 24px 20px',
        background: 'var(--bf-surface)',
        border: '1px solid var(--bf-rule)',
        borderRadius: 14,
        textDecoration: 'none',
        color: 'var(--bf-ink)',
        transition: 'border-color 0.18s, transform 0.18s, box-shadow 0.18s',
        height: '100%',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = 'color-mix(in oklab, var(--bf-accent) 55%, transparent)';
        e.currentTarget.style.transform = 'translateY(-3px)';
        e.currentTarget.style.boxShadow = '0 18px 44px -22px rgba(240,31,61,0.5)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = 'var(--bf-rule)';
        e.currentTarget.style.transform = 'none';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 14,
        }}
      >
        <span
          style={{
            fontFamily: 'var(--bf-mono)',
            fontSize: 11,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: 'var(--bf-ink)',
          }}
        >
          {item.outlet}
        </span>
        <span className="bf-tag" style={{ fontSize: 10 }}>{catLabel}</span>
      </div>

      <h4
        style={{
          fontFamily: 'var(--bf-sans)',
          fontWeight: 600,
          fontSize: 19,
          letterSpacing: '-0.02em',
          lineHeight: 1.2,
          color: 'var(--bf-ink)',
          marginBottom: 12,
          flex: '0 0 auto',
        }}
      >
        {t(item.titleKey, item.titleFallback)}
      </h4>

      <p
        className="bf-body-p"
        style={{ fontSize: 14, color: 'var(--bf-muted)', flex: '1 1 auto', lineHeight: 1.5 }}
      >
        {t(item.descKey, item.descFallback)}
      </p>

      <div
        style={{
          marginTop: 18,
          paddingTop: 14,
          borderTop: '1px solid var(--bf-rule)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontFamily: 'var(--bf-mono)',
          fontSize: 11,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          color: 'var(--bf-muted)',
        }}
      >
        <span>{item.date}</span>
        <span style={{ color: 'var(--bf-accent)' }}>
          {t('press.readArticle', 'Read article')} →
        </span>
      </div>
    </a>
  );
};

const PressSection = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith('pt') ? 'pt' : 'en';

  return (
    <section
      id="press"
      className="bf-section"
      style={{
        background: 'var(--bf-bg-alt)',
        borderTop: '1px solid var(--bf-rule)',
        borderBottom: '1px solid var(--bf-rule)',
      }}
    >
      <div className="bf-container">
        <div className="bf-section-head">
          <span className="bf-eyebrow"><span className="dot"></span>{t('press.badge', 'Press coverage')}</span>
          <div>
            <h2>
              {t('press.titleA', 'What the Portuguese and European')}
              <br />
              <span className="bf-grad-text">{t('press.titleB', 'press have written about us.')}</span>
            </h2>
          </div>
        </div>

        <div
          className="bf-press-grid"
          style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24 }}
        >
          {PRESS_ITEMS.slice(0, 3).map((item) => (
            <PressCard key={item.id} item={item} t={t} lang={lang} />
          ))}
        </div>
        <div
          className="bf-press-grid-2"
          style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 24, marginTop: 24, maxWidth: 820, marginLeft: 'auto', marginRight: 'auto' }}
        >
          {PRESS_ITEMS.slice(3).map((item) => (
            <PressCard key={item.id} item={item} t={t} lang={lang} />
          ))}
        </div>
      </div>

      <style>{`
        @media (max-width: 900px) {
          .bf-press-grid { grid-template-columns: 1fr 1fr !important; }
          .bf-press-grid-2 { grid-template-columns: 1fr 1fr !important; }
        }
        @media (max-width: 600px) {
          .bf-press-grid, .bf-press-grid-2 { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </section>
  );
};

export default PressSection;
